import * as React from "react"
import { CheckIcon, ChevronDown, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface MultiSelectOption {
  value: string
  label: string
}

export interface MultiSelectProps {
  /** Currently selected values */
  value: string[]
  /** Callback when selection changes */
  onChange: (value: string[]) => void
  /** Available options to select from */
  options: MultiSelectOption[]
  /** Placeholder when no items selected */
  placeholder?: string
  /** Placeholder for the search input */
  searchPlaceholder?: string
  /** Whether the component is disabled */
  disabled?: boolean
  /** Additional class names */
  className?: string
  /** ID for accessibility */
  id?: string
}

/**
 * A reusable multi-select dropdown component.
 * Uses Popover + Command pattern for a searchable list with checkboxes.
 */
const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      value,
      onChange,
      options,
      placeholder = "Select items...",
      searchPlaceholder = "Search...",
      disabled = false,
      className,
      id,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false)

    const handleSelect = React.useCallback(
      (optionValue: string) => {
        if (value.includes(optionValue)) {
          // Remove if already selected
          onChange(value.filter((v) => v !== optionValue))
        } else {
          // Add to selection
          onChange([...value, optionValue])
        }
      },
      [value, onChange]
    )

    const handleRemove = React.useCallback(
      (optionValue: string, e: React.MouseEvent) => {
        e.stopPropagation()
        onChange(value.filter((v) => v !== optionValue))
      },
      [value, onChange]
    )

    // Get labels for selected values
    const selectedLabels = React.useMemo(() => {
      const labelMap = new Map(options.map((o) => [o.value, o.label]))
      return value.map((v) => ({
        value: v,
        label: labelMap.get(v) ?? v,
      }))
    }, [value, options])

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            ref={ref}
            id={id}
            role="combobox"
            aria-expanded={open}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            className={cn(
              "flex min-h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer",
              disabled && "cursor-not-allowed opacity-50 pointer-events-none",
              className
            )}
          >
          <div className="flex flex-wrap gap-1 flex-1">
            {selectedLabels.length > 0 ? (
              selectedLabels.map((item) => (
                <Badge
                  key={item.value}
                  variant="secondary"
                  className="flex items-center gap-1 pr-1"
                >
                  {item.label}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={(e) => handleRemove(item.value, e)}
                      className="ml-1 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
                      aria-label={`Remove ${item.label}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
          </div>
        </PopoverTrigger>

        <PopoverContent
          collisionPadding={10}
          side="bottom"
          className="min-w-[--radix-popper-anchor-width] p-0"
        >
          <Command className="w-full max-h-[200px] sm:max-h-[270px]">
            <CommandList>
              <div className="sticky top-0 z-10 bg-popover">
                <CommandInput placeholder={searchPlaceholder} />
              </div>
              <CommandEmpty>No options found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = value.includes(option.value)
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => handleSelect(option.value)}
                      className="flex items-center gap-2"
                    >
                      <div
                        className={cn(
                          "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50"
                        )}
                      >
                        {isSelected && <CheckIcon className="h-3 w-3" />}
                      </div>
                      <span>{option.label}</span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }
)

MultiSelect.displayName = "MultiSelect"

export { MultiSelect }
